import { HelpApiEnvelope } from '../models/help-api.types';

export function helpUnwrapResponse<T>(res: HelpApiEnvelope<T>): T {
  const ok = res?.status === true || res?.status_code === 200;
  if (!ok) {
    throw new Error(res?.message || 'Request failed');
  }
  if (res.data === undefined || res.data === null) {
    throw new Error(res?.message || 'No data in response');
  }
  return res.data as T;
}

/** Success responses where `data` may be omitted (e.g. some DELETE/publish payloads). */
export function helpUnwrapResponseLoose<T>(res: HelpApiEnvelope<T>): T | null {
  const ok = res?.status === true || res?.status_code === 200;
  if (!ok) {
    throw new Error(res?.message || 'Request failed');
  }
  if (res.data === undefined || res.data === null) {
    return null;
  }
  return res.data as T;
}

export function helpNormalizeHttpError(err: unknown): Error {
  if (err && typeof err === 'object' && 'error' in err) {
    const e = err as { error?: HelpApiEnvelope };
    const body = e.error;
    if (body?.message) {
      return new Error(body.message);
    }
  }
  if (err instanceof Error) {
    return err;
  }
  return new Error(String(err));
}

/**
 * Turns API validation text such as `"steps[0].target_key" is not allowed to be empty` into
 * user-facing copy (1-based step numbers, no bracket notation).
 */
export function helpFormatWalkthroughSaveErrorMessage(raw: string | undefined): string {
  const s = raw?.trim();
  if (!s) {
    return '';
  }

  const patterns: RegExp[] = [
    /"steps\[(\d+)\]\.(\w+)"\s+is not allowed to be empty\.?/gi,
    /steps\[(\d+)\]\.(\w+)\s+is not allowed to be empty\.?/gi,
  ];

  const lines: string[] = [];
  const seen = new Set<string>();

  for (const re of patterns) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(s)) !== null) {
      const stepIdx = parseInt(m[1], 10);
      const field = m[2];
      const key = `${m[1]}:${field}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      const stepLabel = `Step ${stepIdx + 1}`;
      switch (field) {
        case 'target_key':
          lines.push(`${stepLabel}: Select a help guide target.`);
          break;
        case 'title':
          lines.push(`${stepLabel}: Enter a title for this step.`);
          break;
        case 'body':
          lines.push(`${stepLabel}: Enter the text for this step.`);
          break;
        default:
          lines.push(`${stepLabel}: "${field}" cannot be empty.`);
      }
    }
    if (lines.length > 0) {
      break;
    }
  }

  if (lines.length > 0) {
    return lines.join('\n');
  }
  return s;
}
